import { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  NO_REFETCH_OPTIONS,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { ConnectQueryKeys } from '~/connect/api';
import { ContainerOrderQueryKeys } from '~/container-order/api';
import { ISupplierContainerOrderGetOneById } from '~/container-order/types';

export type IDistinctCustomerCity = string;
export type IDistinctCustomerCountry = string;
export type ICustomerAutocomplete = { id: string; name: string };

type Options = {
  listDistinctCities?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IDistinctCustomerCity[]>,
      IErrorApiResponse
    >
  >;
  listDistinctCountries?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IDistinctCustomerCountry[]>,
      IErrorApiResponse
    >
  >;
  autocompleteCustomers?: { enabled: boolean };

  accept?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    {
      id: string;
    }
  >;
  reject?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    {
      id: string;
      comment?: string;
    }
  >;
  patch?: UseMutationOptions<
    ISuccessApiResponse<ISupplierContainerOrderGetOneById>,
    IErrorApiResponse,
    {
      id: string;
      key: string;
      value: unknown;
    }
  >;
};

export const useManageSupplierContainerOrders = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions & {
    context: {
      userId: string;
    };
  },
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const listDistinctCities = useQuery({
    queryKey: ConnectQueryKeys.users.containerOrderDistinctCities(
      options.context.userId,
    ),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<IDistinctCustomerCity[]>>(
        `/offers/api/tenants/${tenantCode}/users/${options.context.userId}/container-orders/cities`,
      );
    },
    ...NO_REFETCH_OPTIONS,
    ...options.listDistinctCities,
  });

  const listDistinctCountries = useQuery({
    queryKey: ConnectQueryKeys.users.containerOrderDistinctCountries(
      options.context.userId,
    ),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<IDistinctCustomerCountry[]>>(
        `/offers/api/tenants/${tenantCode}/users/${options.context.userId}/container-orders/countries`,
      );
    },
    ...NO_REFETCH_OPTIONS,
    ...options.listDistinctCountries,
  });

  const autocompleteCustomers = async (
    query: string,
    autocompleteOptions?: { limit?: number; signal?: AbortSignal },
  ) => {
    if (query === '') {
      return [];
    }

    const response = await callCoreApi<
      ISuccessApiResponse<ICustomerAutocomplete[]>
    >(
      `/offers/api/tenants/${tenantCode}/users/${options.context.userId}/container-orders/customers/autocomplete?query=${encodeURIComponent(query)}&limit=${autocompleteOptions?.limit || 10}`,
      { signal: autocompleteOptions?.signal },
    );

    return response.data.map((customer) => ({
      label: customer.name,
      value: customer.id,
    }));
  };

  const acceptMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/users/${options.context.userId}/container-orders/${variables.id}/accept`,
        {
          method: 'PATCH',
        },
      ),
    ...options.accept,
    keysToInvalidateOnSuccess: [ContainerOrderQueryKeys.containerOrders.base()],
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, ...variables }) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/users/${options.context.userId}/container-orders/${id}/reject`,
        {
          method: 'PATCH',
          body: JSON.stringify(variables),
        },
      ),
    ...options.reject,
    keysToInvalidateOnSuccess: [ContainerOrderQueryKeys.containerOrders.base()],
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, key, value }) =>
      callCoreApi<ISuccessApiResponse<ISupplierContainerOrderGetOneById>>(
        `/offers/api/tenants/${tenantCode}/users/${options.context.userId}/container-orders/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ [key]: value }),
        },
      ),
    ...options.patch,
    keysToInvalidateOnSuccess: [ContainerOrderQueryKeys.containerOrders.base()],
  });

  return restrictApi(options, {
    listDistinctCities,
    listDistinctCountries,
    autocompleteCustomers,
    accept: acceptMutation,
    reject: rejectMutation,
    patch: patchMutation,
  });
};
